import { memo, useMemo } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import InsightsMetricRow from "./InsightsMetricRow";
import InsightsProgramAlertRow from "./InsightsProgramAlertRow";
import InsightsLiftBalanceSection from "./InsightsLiftBalanceSection";

/**
 * Footer under per-lift cards: combined volume, lift balance (PL / Olympic / Streetlifting),
 * coverage alerts. Strongman: volume + coverage only (no lift balance in v1).
 */
function InsightsPowerliftingWeekSummary({ summary, lensId }) {
  const { t } = useTranslation("athlete-portal");
  const p = "programs.programInsights.panel";

  const volumeDisplay = summary.volumeDisplay ?? summary.sbdVolumeDisplay;
  const volumeLabelKey = summary.volumeLabelKey ?? "sbdVolumeLabel";
  const volumeInfoKey = summary.volumeInfoKey ?? "sbdVolumeInfo";

  const unprogrammedLiftIds = summary.unprogrammedLiftIds ?? [];
  const liftBalance = summary.liftBalance ?? null;

  /**
   * Coverage alerts: full coverage badge + unprogrammed lift warnings.
   * @returns {object[]}
   */
  const coverageAlerts = useMemo(() => {
    /** @type {{ key: string, variant: "success"|"warning", body: string }[]} */
    const alerts = [];
    if (summary.coverageVariant) {
      alerts.push({
        key: `coverage-${summary.coverageVariant}`,
        variant: "success",
        body: t(`${p}.coverage.${summary.coverageVariant}`),
      });
    }
    for (const liftId of unprogrammedLiftIds) {
      alerts.push({
        key: `unprogrammed-${liftId}`,
        variant: "warning",
        body: t(`${p}.olympicFooterUnprogrammedLifts`, {
          liftNames: t(`${p}.lifts.${liftId}.name`),
        }),
      });
    }
    return alerts;
  }, [summary.coverageVariant, unprogrammedLiftIds, t]);

  return (
    <div className="ProgramInsights-powerliftingWeekSummary">
      <InsightsMetricRow
        label={t(`${p}.${volumeLabelKey}`)}
        valueDisplay={volumeDisplay}
        infoAriaLabel={t(`${p}.${volumeInfoKey}`)}
        infoTooltipBody={t(`${p}.${volumeInfoKey}`)}
      />
      {liftBalance ? (
        <InsightsLiftBalanceSection
          liftBalance={liftBalance}
          lensId={lensId}
        />
      ) : null}
      {coverageAlerts.length > 0 ? (
        <ul
          className="ProgramInsights-volumeCoverageAlerts"
          aria-label={t(`${p}.volumeCoverage.alertsListAria`)}
        >
          {coverageAlerts.map((alert) => (
            <InsightsProgramAlertRow
              key={alert.key}
              as="li"
              variant={alert.variant}
            >
              {alert.body}
            </InsightsProgramAlertRow>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

InsightsPowerliftingWeekSummary.propTypes = {
  lensId: PropTypes.oneOf([
    "powerlifting",
    "olympic_weightlifting",
    "strongman",
    "streetlifting",
  ]).isRequired,
  summary: PropTypes.shape({
    sbdVolumeDisplay: PropTypes.string,
    volumeDisplay: PropTypes.string,
    volumeLabelKey: PropTypes.string,
    volumeInfoKey: PropTypes.string,
    liftBalance: PropTypes.shape({
      rows: PropTypes.arrayOf(
        PropTypes.shape({
          rowId: PropTypes.string.isRequired,
          pct: PropTypes.number.isRequired,
          pctDisplay: PropTypes.string.isRequired,
        })
      ),
    }),
    coverageVariant: PropTypes.string,
    unprogrammedLiftIds: PropTypes.arrayOf(PropTypes.string),
  }).isRequired,
};

export default memo(InsightsPowerliftingWeekSummary);
